import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { Icon } from "../../design-system/Icon";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type { CredentialsStatus } from "../../ipc/types";

/**
 * Generador de la descripción de la publicación (Desarrollo de Producto). Copywriter experto en
 * conversión para Mercado Libre: a partir del título (+ datos extra) redacta una descripción en
 * 7 bloques. Usa la IA general (Claude/Anthropic). No inventa datos: marca [COMPLETAR: ...].
 */
export function ProductDescriptionStudio(): JSX.Element {
  const client = useIpc();
  const { state: creds } = useIpcQuery<CredentialsStatus>((c) => c.getCredentialsStatus(), []);
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [country, setCountry] = useState("Chile");
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const aiOk = creds.status === "ready" && creds.data.ai.configured;

  const generate = async (): Promise<void> => {
    setBusy(true); setError(null); setText(""); setCopied(false);
    const res = await client.generateProductDescription({ title, context, country });
    setBusy(false);
    if (!res.ok) { setError(res.error.message); return; }
    if (!res.value.ok) { setError(res.value.detail); return; }
    setText(res.value.text);
  };

  const copy = async (): Promise<void> => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* sin portapapeles */ }
  };

  return (
    <Card style={{ marginTop: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="ai" size={18} /> Descripción de la publicación (IA)</span>}
        subtitle="Escribe el título (y lo que sepas): un copywriter experto en Mercado Libre redacta la descripción que vende, en 7 bloques."
      />
      <CardBody>
        {creds.status === "ready" && !aiOk ? (
          <div className="callout warn" style={{ marginBottom: "var(--sp-4)" }}>
            <div className="bar" />
            <div className="body">Falta tu <b className="strong">API de IA</b> para redactar. Configúrala en <Link to="/seguridad">Seguridad → Credenciales</Link>.</div>
          </div>
        ) : null}

        <div className="pd-form">
          <div className="pd-field pd-form__full">
            <span className="pd-field__label">Título del producto</span>
            <input className="pd-input" type="text" value={title} aria-label="Título del producto" placeholder="Ej: Termo Acero Inoxidable 1L Doble Pared" onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="pd-field pd-form__full">
            <span className="pd-field__label">Datos extra (opcional)</span>
            <textarea className="pd-input" rows={4} value={context} aria-label="Datos extra"
              placeholder="Precio, material, público objetivo, uso principal, diferencial, garantía, medidas, colores, contenido del paquete… Lo que no pongas se marcará [COMPLETAR] para que lo edites."
              onChange={(e) => setContext(e.target.value)} style={{ resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <div className="pd-field">
            <span className="pd-field__label">País (tono)</span>
            <input className="pd-input" type="text" value={country} aria-label="País" placeholder="Chile" onChange={(e) => setCountry(e.target.value)} />
          </div>
        </div>

        <div className="pd-actions">
          <Button icon="ai" disabled={busy || title.trim().length === 0} onClick={generate}>{busy ? "Redactando…" : "Generar descripción"}</Button>
        </div>

        {error ? (
          <div className="pd-sim__result" style={{ marginTop: "var(--sp-3)" }}>
            <Badge tone="danger" dot>Error</Badge><span className="muted" style={{ fontSize: 13.5 }}>{error}</span>
          </div>
        ) : null}

        {text ? (
          <div style={{ marginTop: "var(--sp-4)" }}>
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span className="strong" style={{ fontSize: 13.5 }}>Descripción lista</span>
              <Button variant="secondary" size="sm" icon="check" onClick={copy}>{copied ? "¡Copiada!" : "Copiar"}</Button>
            </div>
            <textarea
              className="pd-input" aria-label="Descripción generada" value={text} onChange={(e) => setText(e.target.value)}
              rows={18} style={{ width: "100%", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5, whiteSpace: "pre-wrap" }}
            />
            <p className="faint" style={{ fontSize: 12, marginTop: 6 }}>Podés editarla aquí mismo antes de pegarla en Mercado Libre. Revisá los [COMPLETAR: …] y reemplazalos con tus datos reales.</p>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
